import HTMLParsedElement from "html-parsed-element"
import toggleAttribute from "./toggleAttribute"
import { morphdomHold } from "web-ui/morphdom/hold"
import { morphdomPreserveAttributes } from "web-ui/morphdom/preserve-attributes"
import { afterPageRender } from "web-ui/after-page-render"
import * as Log from "web-ui/log"

/*
PersistentFooter is a custom element that creates a footer which sticks to the
bottom of the viewport when the user scrolls up, but returns to its natural
position when the user scrolls down to the actual bottom of the content. This
provides a consistent action area (e.g., form submit buttons) while still
allowing the footer to be part of the document flow when naturally visible.

The footer has several attribute-based states managed by CSS (see
style/foundation/_persistent-footer.scss). When "undocked", the footer is in its
natural document flow position using relative positioning. This is the state
when the bottom of the content is visible. When "docked", the footer sticks to
the bottom of the viewport using sticky positioning. This happens when the user
has scrolled up and the natural footer position is below the viewport.

The "constrained" attribute is a modifier to "docked" that limits how far up the
footer can appear. When both docked and constrained are set, the footer uses
relative positioning with a top offset calculated to ensure a minimum amount of
content remains visible above the footer. This prevents the footer from
overlapping too much content on short viewports or when there are multiple
persistent footers on one page. The "loaded" attribute is added
after the initial render to enable CSS transitions, preventing flash/animation
during initial page load.

Two invisible sentinel elements are created to detect scroll position. The
docking sentinel is positioned absolutely at the bottom of the parent,
immediately after the footer. An IntersectionObserver watches this element; when
the sentinel is visible (its top is above the viewport bottom), the footer
undocks, and when invisible, the footer docks. The constraint sentinel is
positioned absolutely at the top of the parent, offset by min-content-height
plus footer-height. An IntersectionObserver watches this element; when the
sentinel extends below the viewport (meaning there isn't enough viewport space
to show the minimum content with a docked footer), the footer becomes
constrained to avoid overlapping essential content.

The JavaScript sets CSS variables on both the footer and constraint sentinel for
use in CSS positioning calculations. The --persistent-footer-content-height
variable stores the container's total height (offsetParent.offsetHeight). The
--persistent-footer-height variable stores the footer's rendered height. The
--persistent-footer-min-content-height variable comes from the min-content-height
attribute and specifies how much content must remain visible above a constrained
footer.

When docked and constrained, the footer uses relative positioning with top set
to min-content-height plus footer-height minus content-height. Since the footer
is normally at the bottom of the container, this shifts it up so that its top
edge appears at exactly min-content-height from the top of the container. The
constraint sentinel is positioned at min-content-height plus footer-height from
the top, marking the point where the bottom of the footer would be if positioned
at the minimum content height.

Three observers keep the footer state updated: an IntersectionObserver watches
the docking sentinel to detect when it becomes visible or hidden, another
IntersectionObserver watches the constraint sentinel to detect when there's
enough viewport space for minimum content, and a ResizeObserver watches both the
footer and its offsetParent to recalculate positions when sizes change.

The min-content-height attribute is optional. If omitted, the footer will
dock/undock based solely on whether its natural position is visible.
*/
class PersistentFooter extends HTMLParsedElement {
  static get observedAttributes() {
    return ["min-content-height"]
  }

  constructor() {
    super()

    this.dockingObserver = new IntersectionObserver(() => {
      this.updateDocking()
    }, { threshold: 1 })

    this.constraintObserver = new IntersectionObserver(() => {
      this.updateConstraint()
    }, { threshold: 1 })

    this.resizeObserver = new ResizeObserver(() => {
      this.update()
    })
  }

  parsedCallback() {
    // html-parsed-element defers this callback to an animation frame, and
    // Turbo can replace the body again before that frame arrives. When the
    // element was disconnected in the interim, setup is skipped: a detached
    // element has no offsetParent to observe, and its sentinels would leak
    // into the detached tree. - Aaron, Mon Jul 20 2026
    if (!this.isConnected) {
      return
    }

    this.debug("Parsed")

    this.undock()

    this.createSentinelElements()

    this.update()

    this.dockingObserver.observe(this.dockingSentinel)
    this.constraintObserver.observe(this.constraintSentinel)
    this.resizeObserver.observe(this)
    this.resizeObserver.observe(this.offsetParent)

    morphdomPreserveAttributes(this, ["docked", "undocked", "loaded", "constrained", "style"])

    this.initialized = true
  }

  disconnectedCallback() {
    this.dockingObserver.disconnect()
    this.constraintObserver.disconnect()
    this.resizeObserver.disconnect()

    // The sentinels do not exist if the element was disconnected before the
    // deferred parsedCallback ran, which happens when Turbo renders twice in
    // quick succession. - Aaron, Mon Jul 20 2026
    if (this.dockingSentinel) {
      this.dockingSentinel.remove()
    }

    if (this.constraintSentinel) {
      this.constraintSentinel.remove()
    }
  }

  attributeChangedCallback() {
    if (this.initialized) {
      this.update()
    }
  }

  createSentinelElements() {
    this.debug("Creating sentinel elements")

    this.dockingSentinel = document.createElement("div")
    this.dockingSentinel.classList.add("persistent-footer-docking-sentinel")

    // The holds on the sentinels are never released. A morph can discard this
    // footer while it is still visiting the footer's siblings, and the docking
    // sentinel is the next sibling that it visits. That sentinel has already
    // been removed by disconnectedCallback, so the morph would fail to remove
    // it a second time if it were not held. - Shaun, Fri Oct 9 2026
    morphdomHold(this.dockingSentinel)

    this.after(this.dockingSentinel)

    this.constraintSentinel = document.createElement("div")
    this.constraintSentinel.classList.add("persistent-footer-constraint-sentinel")

    morphdomHold(this.constraintSentinel)

    this.parentElement.prepend(this.constraintSentinel)

    this.debug("Created sentinel elements")
  }

  update() {
    const minContentHeight = this.getAttribute("min-content-height")

    if (minContentHeight) {
      this.style.setProperty("--persistent-footer-min-content-height", minContentHeight)
      this.constraintSentinel.style.setProperty("--persistent-footer-min-content-height", minContentHeight)
    } else {
      this.style.removeProperty("--persistent-footer-min-content-height")
      this.constraintSentinel.style.removeProperty("--persistent-footer-min-content-height")
    }

    const contentHeight = this.offsetParent.offsetHeight
    const footerHeight = this.offsetHeight

    this.style.setProperty("--persistent-footer-content-height", `${contentHeight}px`)
    this.style.setProperty("--persistent-footer-height", `${footerHeight}px`)
    this.constraintSentinel.style.setProperty("--persistent-footer-height", `${footerHeight}px`)

    this.updateDocking()
    this.updateConstraint()
  }

  updateDocking() {
    const dockingSentinelBelowWindow = this.dockingSentinel.getBoundingClientRect().top > window.innerHeight

    const minContentHeight = this.constraintSentinel.offsetTop - this.offsetHeight
    const contentHeightExceedsMinHeight = this.offsetTop >= minContentHeight

    const dock = dockingSentinelBelowWindow && contentHeightExceedsMinHeight

    if (dock) {
      this.dock()
    } else {
      this.undock()
    }

    // The "loaded" attribute enables animation, which should not be done until
    // after the initial render so as to avoid a flash
    // - Antoine, Shaun, Mon Mar 17 2025
    if (this.getAttribute("loaded") == null) {
      afterPageRender(() => {
        this.debug("Adding loaded class")

        toggleAttribute(this, "loaded", true)

        this.debug("Added loaded class")
      })
    }
  }

  updateConstraint() {
    const boundingRect = this.constraintSentinel.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const difference = windowHeight - boundingRect.bottom
    const aboveVisibleWindow = difference >= 0

    if (aboveVisibleWindow) {
      this.unconstrain()
    } else {
      this.constrain()
    }
  }

  dock() {
    if (this.hasAttribute("docked")) {
      return
    }

    this.debug("Docking", { classes: this.classList.toString() })

    toggleAttribute(this, "docked", true)
    toggleAttribute(this, "undocked", false)

    this.debug("Docked", { classes: this.classList.toString() })
  }

  undock() {
    if (this.hasAttribute("undocked")) {
      return
    }

    this.debug("Undocking", { classes: this.classList.toString() })

    toggleAttribute(this, "undocked", true)
    toggleAttribute(this, "docked", false)

    this.debug("Undocked", { classes: this.classList.toString() })
  }

  constrain() {
    if (this.hasAttribute("constrained")) {
      return
    }

    this.debug("Constraining", { classes: this.classList.toString() })

    toggleAttribute(this, "constrained", true)

    this.debug("Constrained", { classes: this.classList.toString() })
  }

  unconstrain() {
    if (!this.hasAttribute("constrained")) {
      return
    }

    this.debug("Unconstraining", { classes: this.classList.toString() })

    toggleAttribute(this, "constrained", false)

    this.debug("Unconstrained", { classes: this.classList.toString() })
  }

  debug(message, data) {
    Log.debug("Persistent Footer - " + message, data)
  }
}

if (!window.customElements.get("persistent-footer")) {
  window.PersistentFooter = PersistentFooter
  window.customElements.define("persistent-footer", PersistentFooter)
}
